// The saloon car: where every run starts and ends. The posse spawns in it, walks around, talks
// to Pop, picks gear, readies up; then the launch cinematic blasts them through the roof and
// they drop onto the island. A wipe brings them back to the bar, mid-bite (STORY_BIBLE §7).
//
// The car is real collision in the same World as the island, parked high above the map (inside
// the int16 snapshot range), so walking around in it uses the normal predicted movement and the
// posse replicates like it does in a fight. Its layout lives here so the renderer, the
// collision and the stage logic all read the same numbers.
//
// Stages: Saloon -> (everyone ready) -> Launch (cinematic, cowboys held) -> Run (dropped in) ->
// (run ends: onRunEnd) -> Saloon. Stage changes go out as reliable events (Ev.Stage), so the
// snapshot format is untouched.

import { secs } from './core';
import { CLASSES } from './defs';
import { Ev } from './codec';
import type { Box, Cyl } from './world';
import { InputFrame, Move, Phase, PlayerState, StepOut, newStepOut, respawnAt, stepPlayer } from './player';

export const enum RunStage { Saloon = 0, Launch = 1, Run = 2 }
/** why the posse is (back) in the saloon: first boarding, a wipe (hotdog scene) or an extraction */
export const enum SaloonReason { Board = 0, Wipe = 1, Extract = 2 }

/** World position of the car's floor centre. Must stay inside ±327 m (int16 cm on the wire). */
export const SALOON = { x: 0, y: 60, z: -300 }; // under the 95 m haze ceiling and above KILL_Y (sim/player.ts)

/** Interior layout in car-local metres: X across (+X = bar side), Y up from the floor, Z along. */
export const CAR = {
  hw: 2.8, // interior half width
  hl: 9.5, // interior half length
  h: 3.3, // side ceiling height (collision ceiling)
  skyH: 4.1, // the skylight clerestory down the spine
  skyHW: 0.85, // its half width
  wall: 0.25,
  // the bar runs along +X, from mid-car to the far (fore, -Z) end; you board at the aft (+Z) door
  barZ0: -6.6, barZ1: 2.4, barX0: 1.05, barX1: 1.72, barTop: 1.07,
  backX0: 2.32, backTop: 0.95,
  stoolX: 0.6, stoolZ0: 1.75, stoolStep: -1.2, stools: 7, stoolTop: 0.74,
  // booth bays along -X: two benches facing over a table
  boothX0: -2.8, boothX1: -1.55, bays: [-6.6, -3.2, 0.2, 3.6] as readonly number[], bayHalf: 1.45,
  tableTop: 0.76,
  // gear rack on the +X wall aft of the bar, by the boarding door
  rackZ: 5.6, rackHalf: 1.2,
  // Pop's spot behind the bar, and where you stand to talk to him
  popX: 2.02, popZ: -1.55,
  talkX: 0.25, talkZ: -1.55,
  doorHW: 0.62, doorH: 2.35,
} as const;

/** the pot-belly stove against the booth-side wall at the fore end (client/saloon/dressing.ts), car-local; you can't walk through it */
export const STOVE = { x: -1.95, z: -8.78, r: 0.36 };

/** car-local → world */
export const sx = (lx: number) => SALOON.x + lx;
export const sy = (ly: number) => SALOON.y + ly;
export const sz = (lz: number) => SALOON.z + lz;

export function inSaloon(x: number, y: number, z: number) {
  return Math.abs(x - SALOON.x) < CAR.hw + 1 && Math.abs(z - SALOON.z) < CAR.hl + 1 && y > SALOON.y - 3 && y < SALOON.y + CAR.skyH + 2;
}

export function stoolLocal(i: number) {
  return { x: CAR.stoolX, z: CAR.stoolZ0 + i * CAR.stoolStep };
}

/** Where each slot appears on boarding: past the gear rack at the end of the bar, looking down the car with
 *  the bar on the right (the ref's framing). Far enough from the aft wall for the camera boom. */
export function saloonSpawn(slot: number) {
  return { x: sx(-0.55 + (slot & 1) * 0.95), y: sy(0) + 0.02, z: sz(CAR.hl - 4.6 - (slot >> 1) * 1.05) };
}
/** facing down the car (-Z). yaw 0 looks along -Z in this codebase. */
export const SALOON_SPAWN_YAW = 0;

/** Hotdog scene: each slot is already sitting at the bar. Stools 2..5 (the middle of the run). */
export function saloonSeat(slot: number) {
  const s = stoolLocal(2 + (slot & 3));
  return { x: sx(s.x), y: sy(CAR.stoolTop) + 0.02, z: sz(s.z) };
}
/** seated, facing the bar (+X). */
export const SEAT_YAW = -Math.PI / 2;

/**
 * Every place a cowboy can sit, car-local. The bar stools come first (index = stool), then two places
 * on each booth bench. `x/z/top/yaw` is where the body is drawn (the hips over the cushion); the sim
 * keeps the cowboy standing on solid collision at `ax/ay/az` (a stool top, a bench's block) so
 * prediction and the server agree, and stands him back up at `ex/ez` in the aisle.
 */
export interface Seat { x: number; z: number; top: number; yaw: number; bar: boolean; ax: number; ay: number; az: number; ex: number; ez: number }
export const SEATS: readonly Seat[] = (() => {
  const out: Seat[] = [];
  for (let i = 0; i < CAR.stools; i++) {
    const s = stoolLocal(i);
    out.push({ x: s.x, z: s.z, top: CAR.stoolTop, yaw: SEAT_YAW, bar: true, ax: s.x, ay: CAR.stoolTop + 0.02, az: s.z, ex: s.x - 0.62, ez: s.z });
  }
  for (const bay of CAR.bays) {
    for (const sgn of [-1, 1]) {
      // the bench block runs bay ± (bayHalf - 0.62 .. bayHalf); you sit with your back to its back rest
      const z = bay + sgn * (CAR.bayHalf - 0.5), az = bay + sgn * (CAR.bayHalf - 0.31);
      for (const x of [-2.36, -1.86]) {
        out.push({ x, z, top: 0.5, yaw: sgn < 0 ? Math.PI : 0, bar: false, ax: x, ay: 1.17, az, ex: -1.15, ez: z });
      }
    }
  }
  return out;
})();
/** "not sitting" on the wire */
export const NO_SEAT = 255;

/** The launch line-up: in the aisle under the skylight, facing the bar, backs to the booths. */
export function launchSpot(slot: number) {
  return { x: sx(-0.2), y: sy(0) + 0.02, z: sz(-0.4 + (slot - 1.5) * 1.25) };
}

/** Seconds from the launch committing to the drop starting. The client cinematic is timed to this. */
export const LAUNCH_SECS = 8.6;
/** how far above the island spawn the posse is released */
export const DROP_HEIGHT = 75;

/** Push the car's collision into a World's obstacle lists. Called from the World constructor so it
 *  survives island swaps. */
export function addSaloonCollision(boxes: Box[], cyls: Cyl[]) {
  const C = CAR, t = C.wall;
  const b = (lx0: number, lx1: number, ly0: number, ly1: number, lz0: number, lz1: number) => boxes.push({
    kind: 'saloon', x: sx((lx0 + lx1) / 2), z: sz((lz0 + lz1) / 2), hx: (lx1 - lx0) / 2, hz: (lz1 - lz0) / 2, y0: sy(ly0), y1: sy(ly1), variant: -1,
  });
  // floor slab
  b(-C.hw - t, C.hw + t, -0.6, 0, -C.hl - t, C.hl + t);
  // long walls, end walls (the doors are dressing: the train beyond them isn't walkable)
  b(C.hw, C.hw + t, 0, C.skyH + 0.4, -C.hl - t, C.hl + t);
  b(-C.hw - t, -C.hw, 0, C.skyH + 0.4, -C.hl - t, C.hl + t);
  b(-C.hw, C.hw, 0, C.skyH + 0.4, C.hl, C.hl + t);
  b(-C.hw, C.hw, 0, C.skyH + 0.4, -C.hl - t, -C.hl);
  // the bar, the back-bar
  b(C.barX0, C.barX1, 0, C.barTop, C.barZ0, C.barZ1);
  b(C.backX0, C.hw, 0, C.backTop, C.barZ0 - 0.3, C.barZ1 + 0.3);
  // the bartender's aisle is closed off at both ends of the bar (a flap at the aft end)
  b(C.barX1, C.backX0, 0, C.barTop, C.barZ1 - 0.12, C.barZ1);
  b(C.barX1, C.backX0, 0, C.barTop, C.barZ0, C.barZ0 + 0.12);
  // gear rack cabinet
  b(C.hw - 0.42, C.hw, 0, 2.2, C.rackZ - C.rackHalf, C.rackZ + C.rackHalf);
  // booths: a bench at each end of every bay, a table between
  for (const z of C.bays) {
    b(C.boothX0, C.boothX1, 0, 1.15, z - C.bayHalf, z - C.bayHalf + 0.62);
    b(C.boothX0, C.boothX1, 0, 1.15, z + C.bayHalf - 0.62, z + C.bayHalf);
    b(C.boothX0, -1.85, 0, C.tableTop, z - 0.42, z + 0.42);
  }
  // the rounded bar end nearest the door
  cyls.push({ kind: 'saloon', x: sx(C.barX0 + 0.33), z: sz(C.barZ1), r: 0.42, y0: sy(0), y1: sy(C.barTop), variant: -1 });
  // the stove
  cyls.push({ kind: 'saloon', x: sx(STOVE.x), z: sz(STOVE.z), r: STOVE.r, y0: sy(0), y1: sy(1.2), variant: -1 });
  // stools
  for (let i = 0; i < C.stools; i++) {
    const s = stoolLocal(i);
    cyls.push({ kind: 'saloon', x: sx(s.x), z: sz(s.z), r: 0.2, y0: sy(0), y1: sy(C.stoolTop), variant: -1 });
  }
}

/** Clamp a capsule under the car ceiling (called from World.pushOut, so prediction matches). */
export function saloonCeiling(p: { x: number; y: number; z: number; vy?: number }, h: number) {
  if (!inSaloon(p.x, p.y, p.z)) return;
  const lx = Math.abs(p.x - SALOON.x);
  const top = sy(lx < CAR.skyHW ? CAR.skyH : CAR.h) - h;
  if (p.y > top) {
    p.y = top;
    if (p.vy !== undefined && p.vy > 0) p.vy = 0;
  }
}

/** Buttons that do nothing in the saloon (the guns stay holstered, nobody flies, pause doesn't lift). */
// literal: sim/player.ts imports world.ts which imports this file, so Btn may not exist yet at load time
export const SALOON_STRIP = 2 | 8 | 32 | 4 | 256 | 128 | 1024 | 8192 | 16384 | 32768 | 65536; // Fire Melee Special Reload Fly Pause, Throw Hover SpecialWeapon PowerUp Drink

// ------------------------------------------------------------------ the stage machine (server)

/** What the stage machine needs from Game (kept narrow so game.ts can change around it). */
export interface StageHost {
  tick: number;
  players: Map<number, PlayerState>;
  inputs: Map<number, InputFrame>;
  /** older inputs to apply first this tick (the movement lane's in-order input queue catching a client up) */
  catchup: Map<number, InputFrame[]>;
  world: import('./world').World;
  emit(t: Ev, f: number[], s?: string): void;
  /** begin the run on the island (the game loop's startRun) */
  beginRun(): void;
  /** clear any run state left over (robots, bullets, hazards) */
  clearRun(): void;
}

export class SaloonStage {
  stage: RunStage = RunStage.Saloon;
  reason: SaloonReason = SaloonReason.Board;
  /** ticks since the stage began */
  t = 0;
  ready = new Set<number>();
  /** who's sitting where (pid -> SEATS index). Cosmetic, but it has to agree for everyone. */
  seats = new Map<number, number>();
  /** players whose connection is gone (they don't block the posse) */
  absent = new Set<number>();
  private out: StepOut = newStepOut();

  constructor(private host: StageHost) {}

  /** Where a new cowboy appears: the saloon, unless a run is already on. */
  placeNew(p: PlayerState) {
    if (this.stage === RunStage.Run) return;
    this.toSaloon(p, SaloonReason.Board);
  }

  setReady(pid: number, on: boolean) {
    if (this.stage !== RunStage.Saloon) return;
    if (on) this.ready.add(pid); else this.ready.delete(pid);
    this.broadcastReady();
  }

  /** Take a seat (or NO_SEAT to stand). Only in the saloon, only a free seat, only one you're near. */
  sit(pid: number, seat: number) {
    if (this.stage !== RunStage.Saloon) return;
    const p = this.host.players.get(pid);
    if (!p) return;
    const had = this.seats.get(pid);
    if (seat === NO_SEAT || seat >= SEATS.length) {
      if (had === undefined) return;
      const s = SEATS[had];
      placeAt(p, sx(s.ex), sy(0) + 0.02, sz(s.ez));
      this.seats.delete(pid);
      this.host.emit(Ev.Seat, [pid, NO_SEAT]);
      return;
    }
    if (had === seat) return;
    for (const [o, k] of this.seats) if (k === seat && o !== pid) return; // taken
    const s = SEATS[seat];
    // from where you stand: a couple of metres covers walking up to any stool or booth
    if (had === undefined && Math.hypot(p.x - sx(s.x), p.z - sz(s.z)) > 2.6) return;
    this.seats.set(pid, seat);
    placeAt(p, sx(s.ax), sy(s.ay), sz(s.az));
    p.yaw = s.yaw;
    this.host.emit(Ev.Seat, [pid, seat]);
  }

  private clearSeats() {
    for (const pid of this.seats.keys()) this.host.emit(Ev.Seat, [pid, NO_SEAT]);
    this.seats.clear();
  }

  broadcastReady() {
    const { n, need } = this.count();
    this.host.emit(Ev.ReadyState, [n, need]);
  }

  /** current stage to a (re)joining client */
  announce() {
    this.host.emit(Ev.Stage, [this.stage, this.reason, Math.min(65535, this.t)]);
    if (this.stage === RunStage.Saloon) {
      this.broadcastReady();
      for (const [pid, k] of this.seats) this.host.emit(Ev.Seat, [pid, k]);
    }
  }

  private count() {
    let n = 0, need = 0;
    for (const p of this.host.players.values()) {
      if (this.absent.has(p.id)) continue;
      need++;
      if (this.ready.has(p.id)) n++;
    }
    return { n, need };
  }

  remove(pid: number) {
    this.ready.delete(pid);
    if (this.seats.delete(pid)) this.host.emit(Ev.Seat, [pid, NO_SEAT]);
    this.absent.delete(pid);
    if (this.stage === RunStage.Saloon) this.broadcastReady();
  }

  /** Run one tick of the non-run stages. Returns true when Game should skip its run simulation. */
  step(): boolean {
    this.t++;
    if (this.stage === RunStage.Run) return false;
    const h = this.host;
    if (this.stage === RunStage.Saloon) {
      for (const p of h.players.values()) {
        const extra = h.catchup.get(p.id);
        const inp = h.inputs.get(p.id);
        for (const f of extra ? (inp ? [...extra, inp] : extra) : inp ? [inp] : []) {
          f.buttons &= ~SALOON_STRIP;
          // sitting: the cowboy stays put on his seat until he asks to stand (C2S.Sit NO_SEAT)
          if (this.seats.has(p.id)) { f.mx = 0; f.my = 0; f.buttons &= ~SEATED_STRIP; }
          if (p.phase !== Phase.Alive) { p.phase = Phase.Alive; p.hp = 100; }
          stepPlayer(p, f, h.world, this.out);
        }
      }
      h.catchup.clear();
      const { n, need } = this.count();
      if (need > 0 && n === need) this.beginLaunch();
      return true;
    }
    // Launch: the posse is lined up and held while the cinematic plays
    h.catchup.clear();
    for (const p of h.players.values()) {
      const s = launchSpot(p.slot);
      p.x = s.x; p.y = s.y; p.z = s.z; p.vx = p.vy = p.vz = 0;
      p.yaw = -Math.PI / 2; p.move = Move.Ground; p.grounded = 1; p.iframes = 30;
      const inp = h.inputs.get(p.id);
      if (inp) p.prevButtons = inp.buttons;
    }
    if (this.t >= secs(LAUNCH_SECS)) this.beginDrop();
    return true;
  }

  /** No saloon and no run: the island idles with nothing spawning (headless rules tests). */
  skip() { this.stage = RunStage.Run; this.t = 0; this.ready.clear(); }

  /** Straight into the run, no cinematic and no drop (headless tests, bot soaks). */
  startNow() {
    this.clearSeats();
    this.stage = RunStage.Run;
    this.t = 0;
    this.ready.clear();
    this.host.beginRun();
    this.host.emit(Ev.Stage, [RunStage.Run, this.reason, 0]);
  }

  private beginLaunch() {
    this.clearSeats();
    this.stage = RunStage.Launch;
    this.t = 0;
    this.ready.clear();
    this.host.emit(Ev.Stage, [RunStage.Launch, this.reason, 0]);
  }

  private beginDrop() {
    const h = this.host;
    this.stage = RunStage.Run;
    this.t = 0;
    h.beginRun();
    // released high over wherever the run put each cowboy: they fall in and land hero-style
    for (const p of h.players.values()) {
      p.y += DROP_HEIGHT;
      p.vx = p.vz = 0; p.vy = -18;
      p.move = Move.Air; p.grounded = 0;
      p.iframes = secs(5);
    }
    h.emit(Ev.Stage, [RunStage.Run, this.reason, 0]);
  }

  /** The run is over (wipe or extraction): everyone back aboard. */
  returnToSaloon(reason: SaloonReason) {
    const h = this.host;
    h.clearRun();
    this.stage = RunStage.Saloon;
    this.reason = reason;
    this.t = 0;
    this.ready.clear();
    this.seats.clear();
    for (const p of h.players.values()) this.toSaloon(p, reason);
    h.emit(Ev.Stage, [RunStage.Saloon, reason, 0]);
    this.broadcastReady();
    // the wipe return: everyone is already on a stool at the bar, mid-bite (the stage event first, so
    // clients clear the last run's seats before these land)
    if (reason === SaloonReason.Wipe) for (const p of h.players.values()) this.sit(p.id, wipeStool(p.slot));
  }

  private toSaloon(p: PlayerState, reason: SaloonReason) {
    const at = reason === SaloonReason.Wipe ? saloonSeat(p.slot) : saloonSpawn(p.slot);
    respawnAt(p, this.host.world, at);
    // a seated cowboy is stood on the stool top in the sim; the view folds him into the seat
    p.yaw = reason === SaloonReason.Wipe ? SEAT_YAW : SALOON_SPAWN_YAW;
    p.paused = 0; p.skyT = 0;
    // (respawnAt already refilled the loadout: sim/player.ts refillLoadout)
  }
}

/** the stool each slot lands on in the hotdog scene (the middle of the run, as saloonSeat) */
export function wipeStool(slot: number) { return 2 + (slot & 3); }

/** Buttons a seated cowboy can't use: no hopping off the stool by accident (literal: see SALOON_STRIP) */
const SEATED_STRIP = 1 | 16 | 4096 | 512 | 2048; // Jump Dodge Sprint Swap Aim

/** Put a cowboy down somewhere without the respawn's refills: standing, still, nothing fallen. */
function placeAt(p: PlayerState, x: number, y: number, z: number) {
  p.x = x; p.y = y; p.z = z;
  p.vx = p.vy = p.vz = 0;
  p.lastGx = x; p.lastGy = y; p.lastGz = z;
  p.move = Move.Ground; p.grounded = 1;
  p.fallTopY = -1e9; p.stepY = y;
}
